import { FC, useCallback, useId } from "react";
import {
  Box,
  Checkbox,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { get, useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { IOption } from "../../components/analytics/interfaces";
import { reduceGroups } from "./utils";
import { StyledFormControl, StyledSelect } from "./styled";

export interface IFormSelect {
  name: string;
  disabled?: boolean;
  isLoading?: boolean;
  isEnum?: boolean;
  options: IOption[];
  onChange?: (value: any) => Promise<void>;
  isGrouped?: boolean;
  fullWidth?: boolean;
  multiple?: boolean;
  disabledOptions?: (string | number)[];
}

export const StyledFormSelect: FC<IFormSelect> = props => {
  const {
    disabled,
    fullWidth = false,
    isEnum,
    isGrouped,
    isLoading,
    onChange = () => {},
    name,
    options,
    multiple = false,
    disabledOptions = [],
  } = props;

  const { formatMessage } = useIntl();
  const form = useFormContext();
  const instanceId = useId();
  const selectId = `${name}-select-${instanceId}`;
  const labelId = `${selectId}-label`;
  const formValues = form.getValues();
  let value = get(formValues, name) ?? (multiple ? [] : "");

  if (multiple && !Array.isArray(value)) {
    value = value !== null ? [value] : [];
  }

  const error = get(form.formState.errors, name);
  const label = formatMessage({ id: `form.labels.${name}` });

  const handleChange = useCallback(
    async (event: SelectChangeEvent<any>) => {
      const {
        target: { value },
      } = event;
      form.setValue(name, value, { shouldDirty: true });
      await onChange(value);
    },
    [form, name, onChange],
  );

  const renderValue = (selected: any) => {
    if (multiple && Array.isArray(selected)) {
      return (selected as (string | number)[])
        .map(val => {
          const option = options?.find(({ id }) => id === val);
          const title = option?.title;
          return title ? (isEnum ? formatMessage({ id: `enums.${name}.${title}` }) : title) : "";
        })
        .join(", ");
    } else {
      const option = options?.find(({ id }) => id === selected);
      const title = option?.title;
      return title ? (isEnum ? formatMessage({ id: `enums.${name}.${title}` }) : title) : "";
    }
  };

  const renderMenuItem = ({ id, title, hint }: IOption) => {
    const isDisabled = !!(disabledOptions && disabledOptions.includes(id));
    return (
      <MenuItem key={id} value={id} disabled={isDisabled}>
        {multiple && <Checkbox checked={(value as (string | number)[]).indexOf(id) > -1} disabled={isDisabled} />}
        {isEnum ? (
          <FormattedMessage id={`enums.${name}.${title}`} />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography>{title}</Typography>
            {hint ? <FormHelperText sx={{ mt: 0, ml: 1 }}>({hint})</FormHelperText> : null}
          </Box>
        )}
      </MenuItem>
    );
  };

  const renderOptions = () => {
    if (isGrouped) {
      return options?.reduce(reduceGroups, []).map(({ group, options }) => [
        <ListSubheader
          sx={theme => ({
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.contrastText,
          })}
          key={group}
        >
          <FormattedMessage id={`enums.${name}.${group}`} />
        </ListSubheader>,
        options.map(option => renderMenuItem({ ...option, group })),
      ]);
    } else {
      return options?.map(option => renderMenuItem(option));
    }
  };

  return (
    <StyledFormControl fullWidth={fullWidth} disabled={disabled || !options?.length || isLoading}>
      <InputLabel shrink id={labelId}>
        {label}
      </InputLabel>
      <StyledSelect
        labelId={labelId}
        id={selectId}
        value={multiple ? (Array.isArray(value) ? value : value !== null ? [value] : []) : value !== null ? value : ""}
        label={label}
        onChange={handleChange}
        IconComponent={ExpandMore}
        multiple={multiple}
        notched
        renderValue={renderValue}
      >
        {renderOptions()}
      </StyledSelect>
      {error && error.message ? <FormHelperText>{error.message}</FormHelperText> : null}
    </StyledFormControl>
  );
};
